export class JaitClient {
    config;
    ws = null;
    eventHandlers = new Map();
    constructor(config) {
        this.config = config;
    }
    /** Update the auth token (e.g. after login or refresh) */
    setToken(token) {
        this.config.token = token;
    }
    get headers() {
        const h = { "Content-Type": "application/json" };
        if (this.config.token) {
            h["Authorization"] = `Bearer ${this.config.token}`;
        }
        return h;
    }
    // --- REST API ---
    async assessmentRequest(path, body, method) {
        const res = await fetch(this.config.baseUrl + path, {
            headers: this.headers, credentials: "include",
            method: method ?? (body === undefined ? "GET" : "POST"),
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        });
        const data = await res.json();
        if (!res.ok)
            throw new Error(data.error ?? "Assessment request failed");
        return data;
    }
    compareSecurityChecks(beforeRunId, afterRunId) { return this.assessmentRequest("/api/security/comparisons", { beforeRunId, afterRunId }); }
    securityWorkbench() { return this.assessmentRequest("/api/security/workbench"); }
    securityEngineStatus() { return this.assessmentRequest("/api/security/engines"); }
    startSecurityCheck(input) { return this.assessmentRequest("/api/security/checks", input); }
    getSecurityCheck(id) { return this.assessmentRequest("/api/security/checks/" + encodeURIComponent(id)); }
    cancelSecurityCheck(id) { return this.assessmentRequest("/api/security/checks/" + encodeURIComponent(id) + "/cancel", {}); }
    deleteSecurityCheck(id) { return this.assessmentRequest("/api/security/checks/" + encodeURIComponent(id), undefined, "DELETE"); }
    exportSecurityReport(id) { return this.assessmentRequest("/api/security/checks/" + encodeURIComponent(id) + "/report"); }
    getSecurityRemediationPlan(id) { return this.assessmentRequest("/api/security/findings/" + encodeURIComponent(id) + "/plan"); }
    verifySecurityFinding(id) { return this.assessmentRequest("/api/security/findings/" + encodeURIComponent(id) + "/verify", {}); }
    decideSecurityFinding(id, disposition) { return this.assessmentRequest("/api/security/findings/" + encodeURIComponent(id) + "/decision", { disposition }); }
    scheduleSecurityMonitor(input) { return this.assessmentRequest("/api/security/monitors", input); }
    securityAssessments() {
        return this.assessmentRequest("/api/security/assessments");
    }
    createSecurityScope(input) {
        return this.assessmentRequest("/api/security/scopes", input);
    }
    startSecurityAssessment(scopeId) {
        return this.assessmentRequest("/api/security/assessments", { scopeId });
    }
    getSecurityAssessment(id) {
        return this.assessmentRequest("/api/security/assessments/" + encodeURIComponent(id));
    }
    cancelSecurityAssessment(id) {
        return this.assessmentRequest("/api/security/assessments/" + encodeURIComponent(id) + "/cancel", {});
    }
    async health() {
        const res = await fetch(`${this.config.baseUrl}/health`, {
            headers: this.headers,
        });
        return (await res.json());
    }
    async getMessages(sessionId) {
        const res = await fetch(`${this.config.baseUrl}/api/sessions/${sessionId}/messages`, { headers: this.headers });
        return (await res.json());
    }
    async sendMessage(sessionId, content, onDelta, onDone) {
        const res = await fetch(`${this.config.baseUrl}/api/chat`, {
            method: "POST",
            headers: this.headers,
            body: JSON.stringify({ content, sessionId }),
        });
        if (!res.ok || !res.body) {
            throw new Error(`Chat request failed: ${res.status}`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        const processLine = (line, onDelta, onDone) => {
            if (!line.startsWith("data: "))
                return;
            let data;
            try {
                data = JSON.parse(line.slice(6));
            }
            catch {
                return;
            }
            if (data.type === "token" && data.content)
                onDelta(data.content);
            if (data.type === "done")
                onDone();
            if (data.type === "error") {
                throw new Error(data.message ?? "Stream error");
            }
        };
        while (true) {
            const { done, value } = await reader.read();
            if (done)
                break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() ?? "";
            for (const line of lines) {
                processLine(line, onDelta, onDone);
            }
        }
        if (buffer) {
            processLine(buffer, onDelta, onDone);
        }
    }
    // --- WebSocket ---
    connect(sessionId, deviceId) {
        this.ws = new WebSocket(this.config.wsUrl);
        this.ws.onopen = () => {
            this.ws?.send(JSON.stringify({ type: "subscribe", sessionId, deviceId }));
        };
        this.ws.onmessage = (event) => {
            try {
                const wsEvent = JSON.parse(typeof event.data === "string" ? event.data : "");
                // Fire type-specific handlers
                const handlers = this.eventHandlers.get(wsEvent.type);
                if (handlers) {
                    for (const handler of handlers)
                        handler(wsEvent);
                }
                // Fire wildcard handlers
                const wildcardHandlers = this.eventHandlers.get("*");
                if (wildcardHandlers) {
                    for (const handler of wildcardHandlers)
                        handler(wsEvent);
                }
            }
            catch {
                // ignore parse errors
            }
        };
        this.ws.onclose = () => {
            this.ws = null;
        };
    }
    on(type, handler) {
        if (!this.eventHandlers.has(type)) {
            this.eventHandlers.set(type, new Set());
        }
        this.eventHandlers.get(type).add(handler);
        return () => {
            this.eventHandlers.get(type)?.delete(handler);
        };
    }
    disconnect() {
        this.ws?.close();
        this.ws = null;
    }
}
//# sourceMappingURL=client.js.map